import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

export interface DestinationCity {
    id: number;
    name: string;
    country: string;
    countryCode: string;
    lat?: number;
    lng?: number;
}

// SVG Icons for destinations
const BuildingIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4M9 9v.01M9 12v.01M9 15v.01M9 18v.01"/>
    </svg>
);

const CastleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 20v-7.5L20 11l-2 1.5V9l-2-1v5H8V8L6 9v3.5L4 11l-2 1.5V20"/>
        <path d="M4 20h16M12 14v6M8 4h.01M12 4h.01M16 4h.01M8 4v4M12 4v4M16 4v4"/>
    </svg>
);

const PartyIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5.8 11.3L2 22l10.7-3.8M4 3h.01M22 8h.01M15 2h.01M22 20h.01M22 2l-2.2 2.2M8 8l6 6M2 2l8 8"/>
        <circle cx="12" cy="12" r="2"/>
    </svg>
);

const LandmarkIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="4"/>
        <path d="M12 12v9M8 21h8"/>
    </svg>
);

const TowerIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L8 10h8l-4-8zM8 10l-3 11h14l-3-11M12 10v11M9 14h6M9 17h6"/>
    </svg>
);

const BeachIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"/>
        <path d="M22 10a3 3 0 0 0-3-3h-2.2a7 7 0 0 0-1.44-2"/>
    </svg>
);

const MusicIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18V5l12-2v13"/>
        <circle cx="6" cy="18" r="3"/>
        <circle cx="18" cy="16" r="3"/>
    </svg>
);

const ChurchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 21V8a2 2 0 0 0-2-2h-2V3h-4v3H8a2 2 0 0 0-2 2v13"/>
        <path d="M4 21h16M12 6v6M10 9h4"/>
    </svg>
);

const MapPinIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
        <circle cx="12" cy="10" r="3"/>
    </svg>
);

const SearchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.35-4.35"/>
    </svg>
);

const LoaderIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
    </svg>
);

type IconType = 'building' | 'castle' | 'party' | 'landmark' | 'tower' | 'beach' | 'music' | 'church';

const ICON_MAP: Record<IconType, React.FC> = {
    building: BuildingIcon,
    castle: CastleIcon,
    party: PartyIcon,
    landmark: LandmarkIcon,
    tower: TowerIcon,
    beach: BeachIcon,
    music: MusicIcon,
    church: ChurchIcon,
};

interface SuggestedDestination {
    id: string;
    name: string;         // Name to search in API (English)
    displayName?: string; // Translated name for display
    country: string;      // Display country
    description: string;
    iconType: IconType;
}

const MAX_DESTINATIONS = 4;

// Search can return cities or countries (country → itinerary in capital)
type SearchResult =
    | { type: 'city'; id: number; name: string; country: string; countryCode: string; lat?: number; lng?: number }
    | { type: 'country'; countryCode: string; name: string };

interface DestinationSelectProps {
    selected: DestinationCity[];
    onChange: (cities: DestinationCity[]) => void;
    isExpanded?: boolean;
    maxDestinations?: number;
    placeholder?: string;
    suggestedCity?: DestinationCity | null; // For showing a suggested departure (from IP geolocation)
    suggestedLabel?: string; // Label for the suggested city section
    hideSuggestedDestinations?: boolean; // Hide the default suggested destinations (for origin field)
}

const API_BASE = import.meta.env.VITE_API_URL || '';

// Suggested destinations - will be searched by name in API (no hardcoded IDs)
const SUGGESTED_DESTINATIONS: SuggestedDestination[] = [
    { id: 'bucharest', name: 'Bucharest', country: 'Romania', description: 'For sights like Cișmigiu Gardens', iconType: 'building' },
    { id: 'cluj', name: 'Cluj-Napoca', country: 'Romania', description: 'For a trip abroad', iconType: 'castle' },
    { id: 'budapest', name: 'Budapest', country: 'Hungary', description: 'For its bustling nightlife', iconType: 'party' },
    { id: 'rome', name: 'Rome', country: 'Italy', description: 'For sights like Trevi Fountain', iconType: 'landmark' },
    { id: 'paris', name: 'Paris', country: 'France', description: 'For its top-notch dining', iconType: 'tower' },
    { id: 'barcelona', name: 'Barcelona', country: 'Spain', description: 'Popular beach destination', iconType: 'beach' },
    { id: 'vienna', name: 'Vienna', country: 'Austria', description: 'For its classical music scene', iconType: 'music' },
    { id: 'prague', name: 'Prague', country: 'Czech Republic', description: 'For its historic old town', iconType: 'church' },
];

export function DestinationSelect({ selected, onChange, isExpanded = false, maxDestinations = MAX_DESTINATIONS, placeholder, suggestedCity, suggestedLabel, hideSuggestedDestinations = false }: DestinationSelectProps) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<SearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout>>();

    // Focus input when expanded
    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    const searchCities = useCallback(async (q: string) => {
        if (q.length < 2) {
            setSuggestions([]);
            return;
        }

        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/cities/search?q=${encodeURIComponent(q)}&limit=8`);
            if (res.ok) {
                const data = await res.json();
                const cities: SearchResult[] = (data.cities || []).map((c: DestinationCity) => ({ type: 'city' as const, ...c }));
                const countries: SearchResult[] = (data.countries || []).map((c: { countryCode: string; name: string }) => ({ type: 'country' as const, countryCode: c.countryCode, name: c.name }));
                setSuggestions([...cities, ...countries]);
            }
        } catch (err) {
            logger.error('City search failed:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchCities(query), 300);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query, searchCities]);

    const addCity = (city: DestinationCity) => {
        if (selected.length >= maxDestinations) return;
        if (!selected.find(c => c.id === city.id)) {
            onChange([...selected, city]);
        }
        setQuery('');
        setSuggestions([]);
    };

    const removeCity = (cityId: number) => {
        onChange(selected.filter(c => c.id !== cityId));
    };

    const handleCountryClick = async (countryCode: string) => {
        if (selected.length >= maxDestinations) return;
        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/cities/countries/${encodeURIComponent(countryCode)}/capital`);
            if (res.ok) {
                const data = await res.json();
                if (data.city) addCity(data.city);
            }
        } catch (err) {
            logger.error('Capital fetch failed:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSuggestedClick = async (suggestion: SuggestedDestination) => {
        // Always search for the city in the database to get correct ID and data
        logger.log('Searching for suggested city:', suggestion.name);
        setIsLoading(true);
        try {
            const url = `${API_BASE}/api/cities/search?q=${encodeURIComponent(suggestion.name)}&limit=5`;
            logger.log('Fetch URL:', url);
            const res = await fetch(url);
            logger.log('Response status:', res.status);
            if (res.ok) {
                const data = await res.json();
                logger.log('API response:', data);
                if (data.cities && data.cities.length > 0) {
                    // Find the best match (exact name match preferred)
                    const exactMatch = data.cities.find(
                        (c: DestinationCity) => c.name.toLowerCase() === suggestion.name.toLowerCase()
                    );
                    const cityToAdd = exactMatch || data.cities[0];
                    logger.log('Adding city:', cityToAdd);
                    addCity(cityToAdd);
                } else {
                    logger.warn('City not found in database:', suggestion.name);
                }
            } else {
                logger.error('API error:', res.status, res.statusText);
            }
        } catch (err) {
            logger.error('Failed to search for suggested city:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // Translate suggested destinations (keep original name for API search)
    const getTranslatedDestinations = () => {
        return SUGGESTED_DESTINATIONS.map(dest => ({
            ...dest,
            // Keep original English name for API search
            name: dest.name,
            // Add translated display name
            displayName: t(`tripBuilder.suggested_${dest.id}_name`, dest.name),
            description: t(`tripBuilder.suggested_${dest.id}_desc`, dest.description),
        }));
    };

    // Get icon component for a destination
    const getDestinationIcon = (iconType: IconType) => {
        const IconComponent = ICON_MAP[iconType];
        return <IconComponent />;
    };

    if (!isExpanded) {
        return null;
    }

    return (
        <div className="destination-search-container">
            {/* Search Input */}
            <div className="destination-search-wrapper">
                <svg className="destination-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                </svg>
                <input
                    ref={inputRef}
                    type="text"
                    className="destination-search-input"
                    placeholder={placeholder || t('tripBuilder.search_placeholder', 'Search city or country')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </div>

            {/* Selected Cities */}
            {selected.length > 0 && (
                <div className="selected-cities">
                    {selected.map(city => (
                        <span key={city.id} className="city-tag">
                            {city.country ? `${city.country}, ${city.name}` : city.name}
                            <button 
                                type="button" 
                                onClick={() => removeCity(city.id)} 
                                aria-label={t('common.remove', 'Remove')}
                            >
                                ×
                            </button>
                        </span>
                    ))}
                    {selected.length >= maxDestinations && (
                        <span className="destination-limit-hint">
                            {maxDestinations === 1 
                                ? t('tripBuilder.max_destination_one', 'Maximum 1 destination')
                                : t('tripBuilder.max_destinations', 'Maximum {{max}} destinations', { max: maxDestinations })
                            }
                        </span>
                    )}
                </div>
            )}

            {/* Suggested Departure (from IP geolocation) - show when no selection and no search query */}
            {suggestedCity && selected.length === 0 && query.length < 2 && (
                <div className="suggested-departure">
                    <span className="suggested-departure-label">
                        {suggestedLabel || t('tripBuilder.suggested_departure', 'Suggested departure')}
                    </span>
                    <button
                        type="button"
                        className="destination-item suggested-departure-item"
                        onClick={() => addCity(suggestedCity)}
                    >
                        <div className="destination-icon"><MapPinIcon /></div>
                        <div className="destination-info">
                            <span className="destination-name">{suggestedCity.name}</span>
                            {suggestedCity.country && (
                                <span className="destination-description">{suggestedCity.country}</span>
                            )}
                        </div>
                    </button>
                </div>
            )}

            {/* Search Results - hide when at limit so user knows they must remove one */}
            {query.length >= 2 && selected.length < maxDestinations && (
                <div className="suggested-destinations">
                    {isLoading && (
                        <div className="destination-item">
                            <div className="destination-icon"><LoaderIcon /></div>
                            <div className="destination-info">
                                <span className="destination-name">{t('common.loading', 'Loading...')}</span>
                            </div>
                        </div>
                    )}
                    {!isLoading && suggestions.map(item => (
                        item.type === 'city' ? (
                            <button
                                key={`city-${item.id}`}
                                type="button"
                                className="destination-item"
                                onClick={() => addCity(item)}
                            >
                                <div className="destination-icon"><MapPinIcon /></div>
                                <div className="destination-info">
                                    <span className="destination-name">{item.name}</span>
                                    <span className="destination-description">{item.country}</span>
                                </div>
                            </button>
                        ) : (
                            <button
                                key={`country-${item.countryCode}`}
                                type="button"
                                className="destination-item"
                                onClick={() => handleCountryClick(item.countryCode)}
                            >
                                <div className="destination-icon"><MapPinIcon /></div>
                                <div className="destination-info">
                                    <span className="destination-name">{item.name}</span>
                                    <span className="destination-description">{t('tripBuilder.capital_itinerary', 'Itinerary in capital')}</span>
                                </div>
                            </button>
                        )
                    ))}
                    {!isLoading && suggestions.length === 0 && query.length >= 2 && (
                        <div className="destination-item">
                            <div className="destination-icon"><SearchIcon /></div>
                            <div className="destination-info">
                                <span className="destination-name">{t('tripBuilder.no_results', 'No results found')}</span>
                                <span className="destination-description">{t('tripBuilder.try_another', 'Try another search term')}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Suggested Destinations (when no search query) - hide when at limit or when hideSuggestedDestinations is true */}
            {!hideSuggestedDestinations && query.length < 2 && !isLoading && selected.length < maxDestinations && (
                <div className="suggested-destinations">
                    <div className="suggested-header">
                        {t('tripBuilder.suggested_destinations', 'Suggested destinations')}
                    </div>
                    {getTranslatedDestinations().map(dest => (
                        <button
                            key={dest.id}
                            type="button"
                            className="destination-item"
                            onClick={() => handleSuggestedClick(dest)}
                        >
                            <div className="destination-icon">{getDestinationIcon(dest.iconType)}</div>
                            <div className="destination-info">
                                <span className="destination-name">{dest.displayName || dest.name}, {dest.country}</span>
                                <span className="destination-description">{dest.description}</span>
                            </div>
                        </button>
                    ))}
                </div>
            )}
            
            {/* Loading indicator for suggested click */}
            {query.length < 2 && isLoading && (
                <div className="suggested-destinations">
                    <div className="destination-item">
                        <div className="destination-icon"><LoaderIcon /></div>
                        <div className="destination-info">
                            <span className="destination-name">{t('common.loading', 'Loading...')}</span>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
